// =============================================
// brokenOutgoingLinks — inwersja brokenLinksSort.
//
// Score = ile linków W TYM published artykule wskazuje na nody nieopublikowane
// (czyli ile linków JEST broken z perspektywy SOURCE).
//
// Brokenlinks total = ten sam fizyczny problem co getBrokenLinksScores ale view
// from-source-side: pokazujemy "który opublikowany artykuł ma 404 outgoing".
//
// Use case: user widzi że "Co to jest growth experiment? — 4 broken outgoing"
// i ma 2 opcje (manual decyzja, NIE w tym UI):
//   A) Dogeneruj brakujące nody (klik Priorytet linków 🔗)
//   B) Cofnij publikację artykułu (zakładka Publikacje)
//
// Implementacja: read-only, cache 60s. Wzorzec analogiczny do brokenLinksSort.ts.
// =============================================
import { supabase } from "@/integrations/supabase/client";

interface ScoreEntry {
  count: number;
  missingSlugs: string[];
}
type ScoresMap = Map<string, ScoreEntry>;

interface CacheEntry {
  scores: ScoresMap;
  fetchedAt: number;
}

const CACHE_TTL_MS = 60_000;
// Matchuje WSZYSTKIE href (relative + absolute). FIX 2026-06-09 zgodnie z brokenLinksSort.ts:
// po Link Integrity strip pre-publish live targets stają się absolute URL.
const HREF_RE = /href="([^"\s]+)"/g;
// Markery pending (Link Integrity strip pre-publish) — to są broken outgoing przed heal.
const PENDING_MARKER_RE = /data-pending-link="\/([^"]+)"/g;
const cache = new Map<string, CacheEntry>();

/**
 * Wyciągnij slug (bez slash'y) z dowolnego href.
 * Internal = single-segment path (typowy WP postname). null gdy multi-segment.
 */
function extractInternalSlug(rawHref: string): string | null {
  let path = "";
  if (rawHref.startsWith("/")) {
    path = rawHref;
  } else if (/^https?:\/\//.test(rawHref)) {
    try {
      path = new URL(rawHref).pathname;
    } catch {
      return null;
    }
  } else {
    return null;
  }
  const clean = path.replace(/^\/+/, "").replace(/\/+$/, "").split("?")[0].split("#")[0];
  if (!clean || clean.includes("/")) return null;
  return clean;
}

/**
 * Per opublikowany node — ile linków w jego final_html wskazuje na nieopublikowane
 * (i jakie konkretnie slugi są broken).
 *
 * Cache 60s per mapId. CacheKey `${mapId}:broken_outgoing`.
 *
 * Returns Map<sourceNodeId, {count, missingSlugs}>. Tylko published nody z >0
 * broken outgoing są w mapie (treat absence as count=0).
 */
export async function getBrokenOutgoingLinks(mapId: string): Promise<ScoresMap> {
  const cacheKey = `${mapId}:broken_outgoing`;
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.scores;
  }

  const scores: ScoresMap = new Map();

  // 1. Wszystkie nody w mapie (do filtra "internal cluster link" vs "external")
  const { data: nodes, error: nodesErr } = await (supabase as any)
    .from("thematic_nodes")
    .select("id, slug")
    .eq("map_id", mapId);

  if (nodesErr || !nodes) {
    cache.set(cacheKey, { scores, fetchedAt: Date.now() });
    return scores;
  }

  const nodeIds = (nodes as Array<{ id: string }>).map((n) => n.id);
  if (nodeIds.length === 0) {
    cache.set(cacheKey, { scores, fetchedAt: Date.now() });
    return scores;
  }

  // 2. Published nody w mapie + ich final_html
  const { data: published, error: pubErr } = await (supabase as any)
    .from("thematic_node_content")
    .select("node_id, final_html")
    .in("node_id", nodeIds)
    .eq("engine_stage", "published")
    .not("final_html", "is", null);

  if (pubErr || !published) {
    cache.set(cacheKey, { scores, fetchedAt: Date.now() });
    return scores;
  }

  // 3. Slug sets — published (=valid target) i map (= "internal cluster link")
  const publishedNodeIds = new Set(
    (published as Array<{ node_id: string }>).map((p) => p.node_id),
  );
  const allMapSlugs = new Set<string>();
  const publishedSlugs = new Set<string>();
  for (const n of nodes as Array<{ id: string; slug: string | null }>) {
    if (!n.slug) continue;
    allMapSlugs.add(n.slug);
    if (publishedNodeIds.has(n.id)) publishedSlugs.add(n.slug);
  }

  // 4. Per published — parse href (relative + absolute) + markery pending,
  // wyciągnij internal slug, count internal-but-unpublished targets.
  // Markery <span data-pending-link="/slug"> = link czekający na publikację targetu
  // (po Link Integrity strip pre-publish) — to też jest broken outgoing dopóki
  // target nie zostanie opublikowany.
  for (const p of published as Array<{ node_id: string; final_html: string }>) {
    if (!p.final_html) continue;
    const missing = new Set<string>(); // dedup within doc
    // Pass 1: <a href>
    for (const m of p.final_html.matchAll(HREF_RE)) {
      const targetSlug = extractInternalSlug(m[1]);
      if (!targetSlug) continue;
      if (!allMapSlugs.has(targetSlug)) continue;
      if (publishedSlugs.has(targetSlug)) continue;
      missing.add(targetSlug);
    }
    // Pass 2: <span data-pending-link> markery
    for (const m of p.final_html.matchAll(PENDING_MARKER_RE)) {
      const targetSlug = m[1].replace(/\/+$/, "");
      if (!targetSlug) continue;
      if (!allMapSlugs.has(targetSlug)) continue;
      if (publishedSlugs.has(targetSlug)) continue;
      missing.add(targetSlug);
    }
    if (missing.size > 0) {
      scores.set(p.node_id, {
        count: missing.size,
        missingSlugs: Array.from(missing).sort(),
      });
    }
  }

  cache.set(cacheKey, { scores, fetchedAt: Date.now() });
  return scores;
}

/**
 * Agregaty do prezentacji w info card panel.
 */
export interface BrokenOutgoingStats {
  affectedArticles: number;
  totalBrokenLinks: number;
  topOffenderCount: number; // ile broken w artykule z największą liczbą
}

export function computeBrokenOutgoingStats(scores: ScoresMap): BrokenOutgoingStats {
  let affectedArticles = 0;
  let totalBrokenLinks = 0;
  let topOffenderCount = 0;
  for (const entry of scores.values()) {
    if (entry.count <= 0) continue;
    affectedArticles++;
    totalBrokenLinks += entry.count;
    if (entry.count > topOffenderCount) topOffenderCount = entry.count;
  }
  return { affectedArticles, totalBrokenLinks, topOffenderCount };
}

export function invalidateBrokenOutgoingCache(mapId?: string): void {
  if (mapId) {
    cache.delete(`${mapId}:broken_outgoing`);
  } else {
    cache.clear();
  }
}
